import { useId, useRef, useState } from "react";
import { Clipboard, Trash2, Upload } from "lucide-react";
import { resolvePdfDocumentBrand } from "../pdf/PdfDocumentHeader";

/** Reads an uploaded or pasted image in the same persistent format as the main settings. */
function readCompanyImage(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** Shares company settings and the existing custom-logo entitlement between both settings views. */
export function CompanyBrandSettings() {
  const id = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const [, refresh] = useState(0);
  const [error, setError] = useState("");
  const app = globalThis.state;
  const canEditBrand = app.projectLicense?.checkIfProfessionalAbo() ?? false;
  const brand = resolvePdfDocumentBrand(app);
  const permissionHint = canEditBrand ? undefined : "Eigene Firmenangaben nur mit Professional-Abo verfügbar";

  const setLogo = async (file?: Blob) => {
    if (!app.projectLicense?.checkIfProfessionalAbo()) return;
    try {
      if (file && !file.type.startsWith("image/")) throw new Error("Bitte eine Bilddatei auswählen.");
      const logo = file ? await readCompanyImage(file) : "";
      if (!app.projectLicense?.checkIfProfessionalAbo()) return;
      app.settings.companyLogo = logo;
      setError("");
      refresh(value => value + 1);
      await app.settings.calcCompanyLogoSize();
    } catch {
      setError("Das Firmenlogo konnte nicht geladen werden. Bitte eine Bilddatei auswählen.");
    }
  };

  const pasteLogo = async () => {
    if (!app.projectLicense?.checkIfProfessionalAbo()) return;
    try {
      for (const item of await navigator.clipboard.read()) {
        for (const type of item.types) {
          if (type.startsWith("image/")) {
            await setLogo(await item.getType(type));
            return;
          }
        }
      }
      setError("Die Zwischenablage enthält kein Bild.");
    } catch {
      setError("Die Zwischenablage konnte nicht gelesen werden.");
    }
  };

  return <div className="settings-brand settings-form">
    <label htmlFor={`${id}-name`}>Firmenname</label>
    <input id={`${id}-name`} className="input" type="text" placeholder="ESTADA"
      disabled={!canEditBrand} title={permissionHint}
      defaultValue={canEditBrand ? app.settings.companyName || "ESTADA" : "ESTADA"}
      onChange={event => {
        if (app.projectLicense?.checkIfProfessionalAbo()) app.settings.companyName = event.currentTarget.value;
      }} />
    <label htmlFor={`${id}-url`}>Firmen-URL</label>
    <input id={`${id}-url`} className="input" type="text" placeholder="estada.de"
      disabled={!canEditBrand} title={permissionHint}
      defaultValue={canEditBrand ? app.settings.companyUrl || "estada.de" : "estada.de"}
      onChange={event => {
        if (app.projectLicense?.checkIfProfessionalAbo()) app.settings.companyUrl = event.currentTarget.value;
      }} />
    <span>Firmenlogo</span>
    <div className="settings-brand__logo">
      <img src={brand.logoSource} alt="Firmenlogo" className="settings-brand__preview" />
      <div className="settings-actions" title={permissionHint}>
        <button type="button" className="button" aria-label="Firmenlogo entfernen" title="Firmenlogo entfernen"
          disabled={!canEditBrand || !app.settings.companyLogo} onClick={() => void setLogo()}><Trash2 size={18} /></button>
        <button type="button" className="button" aria-label="Firmenlogo aus Zwischenablage" title="Firmenlogo aus Zwischenablage"
          disabled={!canEditBrand} onClick={() => void pasteLogo()}><Clipboard size={18} /></button>
        <button type="button" className="button" aria-label="Firmenlogo hochladen" title="Firmenlogo hochladen"
          disabled={!canEditBrand} onClick={() => fileInput.current?.click()}><Upload size={18} /></button>
          <input ref={fileInput} id={`${id}-logo`} aria-label="Firmenlogo-Datei" type="file" accept="image/*" disabled={!canEditBrand}
            hidden onChange={event => {
              const file = event.currentTarget.files?.[0];
              event.currentTarget.value = "";
              if (file) void setLogo(file);
            }} />
      </div>
    </div>
    {!canEditBrand && <small className="settings-form__hint">{permissionHint}</small>}
    {error && <span className="settings-form__hint" role="alert">{error}</span>}
  </div>;
}
